<template>
  <div class="main-table-wrapper">
    <el-table
      :data="data"
      style="width: 100%">
      <el-table-column
        :label="indexColumnLabel != null ? indexColumnLabel : $tc('general.n0')"
        min-width="55px">
        <template slot-scope="scope">
          {{ (startIndex || 0) + scope.$index + 1 }}.
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('form.image')"
        width="100px">
        <template slot-scope="scope">
          <main-image :src="$file('avatars', scope.row.avatar)" style="width: 75px; height: 75px" />
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('form.first_name')"
        width="300px">
        <template slot-scope="scope">
          <div class="member-name-cell">
            <el-button
              v-if="nameClickable"
              type="text"
              class="member-name-link"
              @click="$emit('name-click', scope.row)">
              {{ scope.row.firstName }} {{ scope.row.lastName }}
            </el-button>
            <template v-else>{{ scope.row.firstName }} {{ scope.row.lastName }}</template>

            <el-tooltip
              v-if="showExpiredDocumentsStatus && isCompetitorDocumentExpired(scope.row)"
              :content="getExpiredDocumentsTooltip(scope.row)"
              placement="top">
              <span class="competitor-expired-dot" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        v-if="showClub"
        :label="$tc('general.club', 1)"
        min-width="140px">
        <template slot-scope="scope">
          {{ scope.row.clubShortName || '—' }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('form.date_of_birth')">
        <template slot-scope="scope">
          {{ scope.row.born | date }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('form.gender')">
        <template slot-scope="scope">
          {{ scope.row.isMale | sex }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('form.belt')">
        <template slot-scope="scope">
          {{ scope.row.belt | belt }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        label="Ukupno medalja"
        width="120px"
        align="center">
        <template slot-scope="scope">
          {{ getTotalMedals(scope.row) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="72px"
        align="center">
        <template #header>
          <span class="medal-count-header" title="Zlatne medalje">
            <span>Broj</span>
            <span class="medal-header-icon">🥇</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['goldMedals', 'medalsGold', 'gold_medals']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="78px"
        align="center">
        <template #header>
          <span class="medal-points-header">
            <span>Poeni</span>
            <span class="medal-header-icon" title="Poeni za zlatne medalje">🥇</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['goldPoints', 'goldMedalPoints', 'pointsGold', 'gold_points']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="72px"
        align="center">
        <template #header>
          <span class="medal-count-header" title="Srebrne medalje">
            <span>Broj</span>
            <span class="medal-header-icon">🥈</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['silverMedals', 'medalsSilver', 'silver_medals']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="78px"
        align="center">
        <template #header>
          <span class="medal-points-header">
            <span>Poeni</span>
            <span class="medal-header-icon" title="Poeni za srebrne medalje">🥈</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['silverPoints', 'silverMedalPoints', 'pointsSilver', 'silver_points']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="72px"
        align="center">
        <template #header>
          <span class="medal-count-header" title="Bronzane medalje">
            <span>Broj</span>
            <span class="medal-header-icon">🥉</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['bronzeMedals', 'medalsBronze', 'bronze_medals']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showMedalStats"
        width="78px"
        align="center">
        <template #header>
          <span class="medal-points-header">
            <span>Poeni</span>
            <span class="medal-header-icon" title="Poeni za bronzane medalje">🥉</span>
          </span>
        </template>
        <template slot-scope="scope">
          {{ getNumericValue(scope.row, ['bronzePoints', 'bronzeMedalPoints', 'pointsBronze', 'bronze_points']) }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="showPoints"
        label="Ukupno poena"
        width="120px"
        align="right">
        <template slot-scope="scope">
          <el-tooltip
            :content="$tc('general.points')"
            placement="top">
            <el-tag
              size="small"
              class="member-points-tag">
              {{ scope.row.earnedPoints != null ? scope.row.earnedPoints : 0 }}
            </el-tag>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
        v-if="showRankColumn && rankColumnAfterPoints"
        :label="rankColumnLabel != null ? rankColumnLabel : $tc('general.rank')"
        min-width="55px">
        <template slot-scope="scope">
          {{ (startIndex || 0) + scope.$index + 1 }}.
        </template>
      </el-table-column>

      <el-table-column
        v-else-if="showRankColumn"
        :label="rankColumnLabel != null ? rankColumnLabel : $tc('general.rank')"
        min-width="55px">
        <template slot-scope="scope">
          {{ (startIndex || 0) + scope.$index + 1 }}.
        </template>
      </el-table-column>

      <el-table-column
        v-if="showIsActive && canEdit"
        :label="$tc('general.isActive')"
        width="100px"
        align="center">
        <template slot-scope="scope">
          <el-switch
            :value="scope.row.isActive"
            @change="updateActiveState(scope.row, $event)" />
        </template>
      </el-table-column>

      <el-table-column
        v-if="canEdit"
        :label="$tc('general.actions')"
        align="right">
        <template slot-scope="scope">
          <el-button icon="el-icon-edit" circle @click="$emit('edit', scope.row)" />
          <el-button type="danger" icon="el-icon-delete" circle @click="beforeRemove(scope.row)" />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'

export default {
  props: {
    data: {
      type: Array,
      default: null
    },

    canEdit: {
      type: Boolean,
      default: false
    },

    customLogic: {
      type: Boolean,
      default: null
    },

    showIsActive: {
      type: Boolean,
      default: false
    },

    showPoints: {
      type: Boolean,
      default: false
    },

    showMedalStats: {
      type: Boolean,
      default: false
    },

    showClub: {
      type: Boolean,
      default: false
    },

    indexColumnLabel: {
      type: String,
      default: null
    },
    startIndex: {
      type: Number,
      default: 0
    },
    showRankColumn: {
      type: Boolean,
      default: false
    },
    rankColumnLabel: {
      type: String,
      default: null
    },
    rankColumnAfterPoints: {
      type: Boolean,
      default: false
    },

    nameClickable: {
      type: Boolean,
      default: false
    },

    showExpiredDocumentsStatus: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getTodayDateString () {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    normalizeDateValue (value) {
      if (!value) return null
      const stringValue = String(value).trim()
      if (!stringValue) return null
      return stringValue.slice(0, 10)
    },
    isDateExpired (value) {
      const normalizedValue = this.normalizeDateValue(value)
      if (!normalizedValue) return false
      return normalizedValue < this.getTodayDateString()
    },
    isInsuranceExpired (member) {
      return this.isDateExpired(member?.insuranceEndDate)
    },
    isMedicalExaminationExpired (member) {
      return this.isDateExpired(member?.medicalExaminationEndDate)
    },
    isInsuranceMissing (member) {
      return !member?.insuranceDocument || !member?.insuranceEndDate
    },
    isMedicalExaminationMissing (member) {
      return !member?.medicalExaminationDocument || !member?.medicalExaminationEndDate
    },
    isCompetitorDocumentExpired (member) {
      return this.isInsuranceExpired(member) ||
        this.isMedicalExaminationExpired(member) ||
        this.isInsuranceMissing(member) ||
        this.isMedicalExaminationMissing(member)
    },
    getExpiredDocumentsTooltip (member) {
      const reasons = []

      if (this.isInsuranceExpired(member)) {
        reasons.push('Osiguranje je isteklo')
      } else if (this.isInsuranceMissing(member)) {
        reasons.push('Nedostaje dokument osiguranja')
      }

      if (this.isMedicalExaminationExpired(member)) {
        reasons.push('Lekarski pregled je istekao')
      } else if (this.isMedicalExaminationMissing(member)) {
        reasons.push('Nedostaje dokument lekarskog pregleda')
      }

      return reasons.join(' i ')
    },
    getNumericValue (row, keys) {
      for (const key of keys) {
        if (row && row[key] != null && row[key] !== '') {
          const value = Number(row[key])
          if (Number.isFinite(value)) return value
        }
      }
      return 0
    },
    getTotalMedals (row) {
      if (row && row.totalMedals != null && row.totalMedals !== '') {
        const total = Number(row.totalMedals)
        if (Number.isFinite(total)) return total
      }
      return this.getNumericValue(row, ['goldMedals', 'medalsGold', 'gold_medals']) +
        this.getNumericValue(row, ['silverMedals', 'medalsSilver', 'silver_medals']) +
        this.getNumericValue(row, ['bronzeMedals', 'medalsBronze', 'bronze_medals'])
    },
    async beforeRemove (member) {
      try {
        await this.$confirm(`Da li si siguran da želiš da ukloniš člana: ${member.firstName} ${member.lastName}`)
        if (this.customLogic) return this.$emit('custom-delete', member)

        this.onDelete(member)
      } catch {}
    },
    async onDelete (member) {
      const loading = this.$loading()
      try {
        await this.$store.dispatch('members/delete', member.id)
        this.$message.success('Uspešno ste uklonili sudiju.')
        this.$emit('delete', member)
      } catch (e) {
        errorHandler(e)
      } finally {
        loading.close()
      }
    },
    async updateActiveState (member, isActive) {
      try {
        const targetIsActive = !!isActive
        const result = await this.$store.dispatch('members/action', {
          id: member.id,
          action: 'setActive',
          payload: { isActive: targetIsActive ? 1 : 0 }
        })

        // Check if the action was successful
        if (result?.success !== false) {
          const responseMember = result?.item || result?.response
          const updatedMember = responseMember && typeof responseMember === 'object'
            ? { ...member, ...responseMember, isActive: targetIsActive }
            : { ...member, isActive: targetIsActive }

          if (updatedMember) {
            const message = this.$tc(targetIsActive ? 'messages.success_activated_member' : 'messages.success_deactivated_member')
            this.$message.success(message)
            this.$emit('update', updatedMember)
          } else {
            // If no updated member in response, try to get from store
            const storeMember = this.$store.getters['members/get'](member.id)
            if (storeMember && !!storeMember.isActive === targetIsActive) {
              this.$emit('update', storeMember)
            } else {
              // If store doesn't have the updated member, force update to revert switch
              this.$forceUpdate()
            }
          }
        } else {
          // Action was not successful, revert the switch
          this.$forceUpdate()
          this.$message.error(this.$tc('messages.error_updating_member'))
        }
      } catch (e) {
        errorHandler(e)
        // Revert the switch on error
        this.$forceUpdate()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.member-name-link {
  padding: 0;
  font-weight: inherit;
}
.member-name-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.competitor-expired-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #f56c6c;
  box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.6);
  animation: competitor-expired-dot-blink 1.2s ease-in-out infinite;
}
.member-points-tag {
  margin-left: 8px;
  vertical-align: middle;
  background-color: #000;
  border-color: #000;
  color: #fff;
}
.medal-header-icon {
  font-size: 18px;
  line-height: 1;
}
.medal-points-header {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.1;
}
.medal-count-header {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.1;
}
@keyframes competitor-expired-dot-blink {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.6);
  }
  50% {
    opacity: 0.35;
    box-shadow: 0 0 0 6px rgba(245, 108, 108, 0);
  }
}
</style>
